import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { USERS_API_URL } from '../../config/apiConfig';
import './RoleManagementScreen.css';

const RoleManagementScreen = () => {
  const { token } = useAuth();
  const [roles, setRoles] = useState([]);
  const [screens, setScreens] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    role_name: '',
    description: '',
    defaultScreenId: '',
    selectedPermissions: {}
  });

  const fetchRoles = useCallback(async () => {
    try {
      const response = await fetch(`${USERS_API_URL}/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      } else {
        throw new Error('Failed to fetch roles');
      }
    } catch (err) {
      setError('Error fetching roles: ' + err.message);
    }
  }, [token]);

  const fetchScreensAndPermissions = useCallback(async () => {
    try {
      // Fetch screens
      const screensResponse = await fetch(`${USERS_API_URL}/screens`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Fetch permissions
      const permissionsResponse = await fetch(`${USERS_API_URL}/permissions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (screensResponse.ok && permissionsResponse.ok) {
        const screensData = await screensResponse.json();
        const permissionsData = await permissionsResponse.json();
        
        setScreens(screensData.screens || []);
        setPermissions(permissionsData.permissions || []);
      } else {
        throw new Error('Failed to fetch screens and permissions');
      }
    } catch (err) {
      setError('Error fetching screens and permissions: ' + err.message);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchRoles(), fetchScreensAndPermissions()]);
      setLoading(false);
    };

    loadData();
  }, [fetchRoles, fetchScreensAndPermissions]);

  const handleCreateRole = () => {
    setEditingRole(null);
    setFormData({
      role_name: '',
      description: '',
      defaultScreenId: '',
      selectedPermissions: {}
    });
    setShowRoleForm(true);
  };

  const handleEditRole = useCallback(async (roleId) => {
    try {
      const response = await fetch(`${USERS_API_URL}/roles/${roleId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const role = await response.json();
        
        // Build selectedPermissions object from role permissions
        const selectedPermissions = {};
        role.permissions.forEach(perm => {
          const key = `${perm.screen_id}-${perm.permission_id}`;
          selectedPermissions[key] = true;
        });

        setEditingRole(role);
        setFormData({
          role_name: role.role_name,
          description: role.description || '',
          defaultScreenId: role.default_screen_id || '',
          selectedPermissions
        });
        setShowRoleForm(true);
      } else {
        throw new Error('Failed to fetch role details');
      }
    } catch (err) {
      setError('Error fetching role details: ' + err.message);
    }
  }, [token]);

  const handleDeleteRole = useCallback(async (roleId, roleName) => {
    if (!window.confirm(`Are you sure you want to delete the role "${roleName}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${USERS_API_URL}/roles/${roleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchRoles();
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete role');
      }
    } catch (err) {
      setError('Error deleting role: ' + err.message);
    }
  }, [token, fetchRoles]);

  const handlePermissionChange = (screenId, permissionId) => {
    // For radio buttons, we need to clear other permissions for this screen
    // and set only the selected permission
    setFormData(prev => {
      const newSelectedPermissions = { ...prev.selectedPermissions };
      
      // Clear all permissions for this screen
      permissions.forEach(perm => {
        const key = `${screenId}-${perm.permission_id}`;
        delete newSelectedPermissions[key];
      });
      
      // Set the selected permission
      const selectedKey = `${screenId}-${permissionId}`;
      newSelectedPermissions[selectedKey] = true;
      
      return {
        ...prev,
        selectedPermissions: newSelectedPermissions
      };
    });
  };

  const getSelectedPermission = (screenId) => {
    // Return the permission_id of the selected permission for this screen
    if (!permissions || !Array.isArray(permissions)) {
      return null;
    }
    
    for (const permission of permissions) {
      const key = `${screenId}-${permission.permission_id}`;
      if (formData.selectedPermissions[key]) {
        return permission.permission_id;
      }
    }
    return null;
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');

    try {
      // Build permissions array from selectedPermissions
      const permissionsArray = [];
      Object.keys(formData.selectedPermissions).forEach(key => {
        if (formData.selectedPermissions[key]) {
          const [screenId, permissionId] = key.split('-');
          permissionsArray.push({
            screen_id: parseInt(screenId),
            permission_id: parseInt(permissionId)
          });
        }
      });

      const payload = {
        role_name: formData.role_name,
        description: formData.description,
        default_screen_id: formData.defaultScreenId ? parseInt(formData.defaultScreenId) : null,
        permissions: permissionsArray
      };

      const url = editingRole 
        ? `${USERS_API_URL}/roles/${editingRole.role_id}`
        : `${USERS_API_URL}/roles`;
      
      const method = editingRole ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setShowRoleForm(false);
        await fetchRoles();
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save role');
      }
    } catch (err) {
      setError('Error saving role: ' + err.message);
    }
  }, [formData, editingRole, token, fetchRoles]);

  const closeForm = () => {
    setShowRoleForm(false);
    setEditingRole(null);
    setError('');
  };

  if (loading) {
    return <div className="role-management-loading">Loading role management...</div>;
  }

  return (
    <div className="role-management-screen">
      <div className="role-management-header">
        <h1>Role Management</h1>
        <button 
          className="create-role-button"
          onClick={handleCreateRole}
        >
          Create New Role
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="roles-table-container">
        <table className="roles-table">
          <thead>
            <tr>
              <th>Role Name</th>
              <th>Description</th>
              <th>User Count</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {roles.map(role => (
              <tr key={role.role_id}>
                <td className="role-name">{role.role_name}</td>
                <td className="role-description">{role.description || '-'}</td>
                <td className="user-count">{role.user_count}</td>
                <td className="role-status">
                  <span className={`status-badge ${role.is_active ? 'active' : 'inactive'}`}>
                    {role.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="created-date">
                  {new Date(role.created_at).toLocaleDateString()}
                </td>
                <td className="role-actions">
                  <button 
                    className="edit-button"
                    onClick={() => handleEditRole(role.role_id)}
                  >
                    Edit
                  </button>
                  {role.role_name !== 'Administrator' && (
                    <button 
                      className="delete-button"
                      onClick={() => handleDeleteRole(role.role_id, role.role_name)}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showRoleForm && (
        <div className="role-form-overlay">
          <div className="role-form-container">
            <div className="role-form-header">
              <h2>{editingRole ? 'Edit Role' : 'Create New Role'}</h2>
              <button className="close-button" onClick={closeForm}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="role-form">
              <div className="form-group">
                <label htmlFor="role_name">Role Name</label>
                <input
                  type="text"
                  id="role_name"
                  value={formData.role_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, role_name: e.target.value }))}
                  required
                  placeholder="Enter role name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter role description"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label htmlFor="default_screen">Default Landing Screen</label>
                <select
                  id="default_screen"
                  value={formData.defaultScreenId}
                  onChange={(e) => setFormData(prev => ({ ...prev, defaultScreenId: e.target.value }))}
                >
                  <option value="">Home Page (Default)</option>
                  {screens.map(screen => (
                    <option key={screen.screen_id} value={screen.screen_id}>
                      {screen.screen_name}{screen.is_public ? ' (Public)' : ''}
                    </option>
                  ))}
                </select>
                <small style={{ color: '#666', fontSize: '0.85em', display: 'block', marginTop: '4px' }}>
                  Select which screen users with this role should be redirected to after login.
                  Public screens are marked with "(Public)" and don't require specific permissions.
                </small>
              </div>

              <div className="permissions-section">
                <h3>Screen Permissions</h3>
                <div className="permissions-grid">
                  {screens.map(screen => (
                    <div key={screen.screen_id} className="screen-permissions">
                      <div className="screen-header">
                        <h4>
                          {screen.screen_name}
                          {screen.is_public && (
                            <span style={{
                              marginLeft: '0.5rem',
                              fontSize: '0.7em',
                              backgroundColor: '#28a745',
                              color: 'white',
                              padding: '0.2rem 0.5rem',
                              borderRadius: '12px',
                              fontWeight: 'bold'
                            }}>
                              PUBLIC
                            </span>
                          )}
                        </h4>
                        <p>{screen.description}</p>
                        {screen.is_public && (
                          <p style={{
                            fontSize: '0.85em',
                            color: '#28a745',
                            fontStyle: 'italic',
                            margin: '0.25rem 0 0 0'
                          }}>
                            This screen is publicly accessible - role permissions are not required
                          </p>
                        )}
                      </div>
                      <div className="permission-checkboxes" style={{
                        opacity: screen.is_public ? 0.5 : 1,
                        pointerEvents: screen.is_public ? 'none' : 'auto'
                      }}>
                        {screen.is_public ? (
                          <div style={{
                            textAlign: 'center',
                            padding: '1rem',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            color: '#6c757d',
                            fontStyle: 'italic'
                          }}>
                            Role permissions not applicable - screen is publicly accessible
                          </div>
                        ) : (
                          <>
                            <div className="permission-option">
                              <label className="permission-radio">
                                <input
                                  type="radio"
                                  name={`screen-${screen.screen_id}`}
                                  value=""
                                  checked={getSelectedPermission(screen.screen_id) === null}
                                  onChange={() => {
                                    // Clear all permissions for this screen (no access)
                                    setFormData(prev => {
                                      const newSelectedPermissions = { ...prev.selectedPermissions };
                                      permissions.forEach(perm => {
                                        const key = `${screen.screen_id}-${perm.permission_id}`;
                                        delete newSelectedPermissions[key];
                                      });
                                      return {
                                        ...prev,
                                        selectedPermissions: newSelectedPermissions
                                      };
                                    });
                                  }}
                                />
                                <span className="permission-label">No Access</span>
                              </label>
                            </div>
                            {(permissions || []).map(permission => (
                              <div key={permission.permission_id} className="permission-option">
                                <label className="permission-radio">
                                  <input
                                    type="radio"
                                    name={`screen-${screen.screen_id}`}
                                    value={permission.permission_id}
                                    checked={getSelectedPermission(screen.screen_id) === permission.permission_id}
                                    onChange={() => handlePermissionChange(screen.screen_id, permission.permission_id)}
                                  />
                                  <span className="permission-label">{permission.permission_name}</span>
                                </label>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-button" onClick={closeForm}>
                  Cancel
                </button>
                <button type="submit" className="save-button">
                  {editingRole ? 'Update Role' : 'Create Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagementScreen;
